import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useParams, Link, useNavigate, useSearchParams } from 'react-router-dom';
import Layout from '../components/Layout';
import ChatPanel from '../components/ChatPanel';
import WebhookPanel from '../components/WebhookPanel';
import VersionHistoryPanel from '../components/VersionHistoryPanel';
import SourceFilesPanel from '../components/SourceFilesPanel';
import FeedbackPanel from '../components/FeedbackPanel';
import { SLOW_AFTER_MIN } from '../components/upload/JobProgressTracker';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { getProject, getDocumentation, deleteDocumentation, getDocumentationById, regenerateDocumentation, getJobStatus, exportDocument, downloadExport } from '../services/api';
import { toast } from 'sonner';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  ArrowLeft, Upload, Trash2, Loader2, FolderOpen, FileText,
  Building2, Calendar, Clock, Users, Wrench, Download, RefreshCw, Send,
  Sparkles, Webhook, PanelLeftClose, PanelLeftOpen, List, ChevronRight,
  History, FileDown, ChevronDown, AlertTriangle, Code2, MessageSquarePlus
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

const API_URL_BASE = process.env.REACT_APP_BACKEND_URL + '/api';

// ---------------------------------------------------------------------------
// Table of Contents — extracts headings from markdown
// ---------------------------------------------------------------------------
const extractHeadings = (content) => {
  if (!content) return [];
  const headings = [];
  const lines = content.split('\n');
  for (const line of lines) {
    const match = line.match(/^(#{1,4})\s+(.+)/);
    if (match) {
      const level = match[1].length;
      const text = match[2].replace(/\*\*/g, '').replace(/\*/g, '').trim();
      const id = text.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
      headings.push({ level, text, id });
    }
  }
  return headings;
};

const TableOfContents = ({ content, activeId }) => {
  const headings = useMemo(() => extractHeadings(content), [content]);
  if (headings.length < 2) return null;

  const handleClick = (id) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <nav className="space-y-0.5" data-testid="table-of-contents">
      <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-widest mb-3 px-2">
        On this page
      </p>
      {headings.map((h, i) => (
        <button
          key={i}
          onClick={() => handleClick(h.id)}
          className={`block w-full text-left text-[13px] leading-snug py-1 px-2 rounded transition-colors truncate ${
            activeId === h.id
              ? 'text-[#2B8FD8] font-medium bg-blue-50/60'
              : 'text-slate-500 hover:text-slate-800 hover:bg-slate-50'
          }`}
          style={{ paddingLeft: `${(h.level - 1) * 12 + 8}px` }}
          title={h.text}
        >
          {h.text}
        </button>
      ))}
    </nav>
  );
};

// ---------------------------------------------------------------------------
// Markdown components — Notion-inspired typography
// ---------------------------------------------------------------------------
const createMarkdownComponents = (onFeedbackClick) => ({
  h1: ({ children }) => {
    const text = typeof children === 'string' ? children : children?.toString() || '';
    const id = text.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
    return (
      <h1 id={id} className="group text-[28px] font-bold text-slate-900 mt-12 mb-5 pb-3 border-b border-slate-200/80 font-['Plus_Jakarta_Sans'] first:mt-0 scroll-mt-36 relative">
        {children}
        {onFeedbackClick && (
          <button
            onClick={(e) => { e.stopPropagation(); onFeedbackClick(text); }}
            className="opacity-0 group-hover:opacity-100 transition-opacity inline-flex items-center gap-1 ml-2 px-1.5 py-0.5 rounded bg-blue-50 text-[#2B8FD8] text-[10px] font-medium hover:bg-blue-100 align-middle"
            title="Leave feedback on this section"
          >
            <MessageSquarePlus className="h-3 w-3" />
          </button>
        )}
      </h1>
    );
  },
  h2: ({ children }) => {
    const text = typeof children === 'string' ? children : children?.toString() || '';
    const id = text.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
    return (
      <h2 id={id} className="group text-[22px] font-semibold text-slate-800 mt-10 mb-4 pb-2 border-b border-slate-100 font-['Plus_Jakarta_Sans'] scroll-mt-36 relative">
        {children}
        {onFeedbackClick && (
          <button
            onClick={(e) => { e.stopPropagation(); onFeedbackClick(text); }}
            className="opacity-0 group-hover:opacity-100 transition-opacity inline-flex items-center gap-1 ml-2 px-1.5 py-0.5 rounded bg-blue-50 text-[#2B8FD8] text-[10px] font-medium hover:bg-blue-100 align-middle"
            title="Leave feedback on this section"
          >
            <MessageSquarePlus className="h-3 w-3" />
          </button>
        )}
      </h2>
    );
  },
  h3: ({ children }) => {
    const text = typeof children === 'string' ? children : children?.toString() || '';
    const id = text.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
    return (
      <h3 id={id} className="group text-lg font-semibold text-slate-800 mt-8 mb-3 font-['Plus_Jakarta_Sans'] scroll-mt-36 relative">
        {children}
        {onFeedbackClick && (
          <button
            onClick={(e) => { e.stopPropagation(); onFeedbackClick(text); }}
            className="opacity-0 group-hover:opacity-100 transition-opacity inline-flex items-center gap-1 ml-2 px-1.5 py-0.5 rounded bg-blue-50 text-[#2B8FD8] text-[10px] font-medium hover:bg-blue-100 align-middle"
            title="Leave feedback on this section"
          >
            <MessageSquarePlus className="h-3 w-3" />
          </button>
        )}
      </h3>
    );
  },
  h4: ({ children }) => {
    const text = typeof children === 'string' ? children : children?.toString() || '';
    const id = text.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
    return (
      <h4 id={id} className="text-base font-semibold text-slate-700 mt-6 mb-2 font-['Plus_Jakarta_Sans'] scroll-mt-36">
        {children}
      </h4>
    );
  },
  p: ({ children }) => (
    <p className="text-[15px] leading-[1.8] text-slate-600 my-4">{children}</p>
  ),
  ul: ({ children }) => (
    <ul className="my-4 ml-1 space-y-2 list-none">{children}</ul>
  ),
  ol: ({ children }) => (
    <ol className="my-4 ml-1 space-y-2 list-decimal list-inside marker:text-slate-400 marker:font-medium">{children}</ol>
  ),
  li: ({ children }) => (
    <li className="text-[15px] leading-[1.8] text-slate-600 pl-1 relative before:content-[''] before:absolute before:left-[-12px] before:top-[11px] before:w-[5px] before:h-[5px] before:rounded-full before:bg-slate-300">{children}</li>
  ),
  blockquote: ({ children }) => (
    <blockquote className="border-l-[3px] border-[#2B8FD8]/40 bg-sky-50/40 pl-5 pr-4 py-3 my-6 rounded-r text-slate-600">
      {children}
    </blockquote>
  ),
  table: ({ children }) => (
    <div className="overflow-x-auto my-8 rounded-lg border border-slate-200">
      <table className="min-w-full text-sm">{children}</table>
    </div>
  ),
  thead: ({ children }) => <thead className="bg-slate-50/80 border-b border-slate-200">{children}</thead>,
  th: ({ children }) => (
    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">{children}</th>
  ),
  td: ({ children }) => (
    <td className="px-4 py-3 text-sm text-slate-600 border-t border-slate-100">{children}</td>
  ),
  tr: ({ children }) => (
    <tr className="hover:bg-slate-50/50 transition-colors">{children}</tr>
  ),
  hr: () => <hr className="my-10 border-slate-200" />,
  a: ({ href, children }) => (
    <a href={href} className="text-[#2B8FD8] hover:text-[#2075b3] underline underline-offset-2 decoration-[#2B8FD8]/30 hover:decoration-[#2B8FD8] transition-colors" target="_blank" rel="noopener noreferrer">
      {children}
    </a>
  ),
  strong: ({ children }) => <strong className="font-semibold text-slate-800">{children}</strong>,
  em: ({ children }) => <em className="text-slate-600">{children}</em>,
  code: ({ inline, className, children }) => {
    if (inline) {
      return (
        <code className="bg-slate-100 text-slate-700 px-1.5 py-0.5 rounded text-[13px] font-mono border border-slate-200/60">
          {children}
        </code>
      );
    }
    const lang = className?.replace('language-', '') || '';
    return (
      <div className="my-6 rounded-lg overflow-hidden border border-slate-200/60 shadow-sm">
        {lang && (
          <div className="bg-slate-800 px-4 py-1.5 text-[10px] font-mono text-slate-400 uppercase tracking-wider border-b border-slate-700/30">
            {lang}
          </div>
        )}
        <pre className="bg-[#1e1e2e] text-slate-200 p-5 overflow-x-auto">
          <code className="text-[13px] leading-6 font-mono">{children}</code>
        </pre>
      </div>
    );
  },
});

// NOTE: markdownComponents is now created dynamically inside the component
// to pass the feedback click handler

// ---------------------------------------------------------------------------
// Main Page Component
// ---------------------------------------------------------------------------
const ProjectDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const requestedDocId = searchParams.get('doc');
  const { canUpload, canDelete, canRequestDelete, canEdit, token } = useAuth();
  const [project, setProject] = useState(null);
  const [documentation, setDocumentation] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDoc, setSelectedDoc] = useState(null);
  // SOW and design documents export branded, which needs editor rights (mirrors the backend's _BRANDED_KINDS)
  const isBrandedDoc = ['sow', 'design'].includes(selectedDoc?.doc_kind);
  const [loadingDoc, setLoadingDoc] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [regenElapsedMin, setRegenElapsedMin] = useState(0);
  const regenTimerRef = useRef(null);
  const [chatOpen, setChatOpen] = useState(false);
  const [webhookOpen, setWebhookOpen] = useState(false);
  const [versionHistoryOpen, setVersionHistoryOpen] = useState(false);
  const [sourceFilesOpen, setSourceFilesOpen] = useState(false);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [feedbackSection, setFeedbackSection] = useState('');
  const [openFeedbackCount, setOpenFeedbackCount] = useState(0);
  const [totalFeedbackCount, setTotalFeedbackCount] = useState(0);
  const [exportMenuOpen, setExportMenuOpen] = useState(false);
  const [activeDocTab, setActiveDocTab] = useState('full');
  const [generatingAudience, setGeneratingAudience] = useState(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(window.innerWidth < 768);
  const [activeTocId, setActiveTocId] = useState('');
  const contentRef = useRef(null);
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;

  const isRightPanelOpen = chatOpen || webhookOpen || versionHistoryOpen || sourceFilesOpen || feedbackOpen;

  // On laptop-class widths there isn't room for nav + doc list + content +
  // TOC + a right panel: auto-collapse the doc list when a panel opens.
  useEffect(() => {
    if (isRightPanelOpen && window.innerWidth < 1536) {
      setSidebarCollapsed(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRightPanelOpen]);

  // Create markdown components with feedback handler
  const handleSectionFeedback = useCallback((sectionText) => {
    setFeedbackSection(sectionText);
    setFeedbackOpen(true);
    setChatOpen(false);
    setWebhookOpen(false);
    setVersionHistoryOpen(false);
    setSourceFilesOpen(false);
  }, []);

  const markdownComponents = useMemo(
    () => createMarkdownComponents(canEdit ? handleSectionFeedback : null),
    [canEdit, handleSectionFeedback]
  );

  // Track active heading for ToC highlight
  useEffect(() => {
    const content = activeDocTab === 'full' ? selectedDoc?.content
      : activeDocTab === 'customer' ? selectedDoc?.customer_content
      : selectedDoc?.internal_content;
    if (!content) return;

    const headings = extractHeadings(content);
    if (headings.length < 2) return;

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setActiveTocId(entry.target.id);
          }
        }
      },
      { rootMargin: '-80px 0px -70% 0px', threshold: 0 }
    );

    const timer = setTimeout(() => {
      headings.forEach(h => {
        const el = document.getElementById(h.id);
        if (el) observer.observe(el);
      });
    }, 200);

    return () => {
      clearTimeout(timer);
      observer.disconnect();
    };
  }, [activeDocTab, selectedDoc?.content, selectedDoc?.customer_content, selectedDoc?.internal_content]);

  // eslint-disable-next-line react-hooks/exhaustive-deps -- loadFullDoc & navigate are stable; fetch on id/requestedDocId change
  useEffect(() => { fetchData(); }, [id, requestedDocId]);

  const fetchData = useCallback(async () => {
    try {
      const [projectRes, docsRes] = await Promise.all([
        getProject(id), getDocumentation(id)
      ]);
      setProject(projectRes.data);
      const sortedDocs = docsRes.data.sort((a, b) => b.version - a.version);
      setDocumentation(sortedDocs);
      // If a specific doc was requested via ?doc= query param, load that
      const targetDoc = requestedDocId
        ? sortedDocs.find(d => d.id === requestedDocId)
        : null;
      // Otherwise prefer is_latest doc, then fall back to highest version
      const docToLoad = targetDoc || sortedDocs.find(d => d.is_latest === true) || sortedDocs[0];
      if (docToLoad) await loadFullDoc(docToLoad.id);
    } catch (error) {
      toast.error('Failed to load project');
      navigate('/projects');
    } finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, requestedDocId]);

  const loadFullDoc = async (docId) => {
    setLoadingDoc(true);
    try {
      const res = await getDocumentationById(docId);
      setSelectedDoc(res.data);
      // Update feedback counts from doc response (Issue 4)
      if (res.data.feedback_counts) {
        setOpenFeedbackCount(res.data.feedback_counts.open || 0);
        setTotalFeedbackCount(res.data.feedback_counts.total || 0);
      }
    } catch (error) { toast.error('Failed to load documentation'); }
    finally { setLoadingDoc(false); }
  };

  const handleSelectDoc = async (doc) => {
    if (selectedDoc?.id === doc.id) return;
    setActiveDocTab('full');
    await loadFullDoc(doc.id);
  };

  const handleDocUpdated = async () => {
    if (selectedDoc) await loadFullDoc(selectedDoc.id);
  };

  const handleFeedbackCountChange = useCallback((openCount, totalCount) => {
    setOpenFeedbackCount(openCount);
    setTotalFeedbackCount(totalCount);
  }, []);

  // Scroll to section anchor from URL hash (e.g. #error-handling) after doc renders
  useEffect(() => {
    if (selectedDoc && window.location.hash) {
      const targetId = window.location.hash.slice(1);
      // Wait for markdown to render, then scroll
      const timer = setTimeout(() => {
        const el = document.getElementById(targetId);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 600);
      return () => clearTimeout(timer);
    }
  }, [selectedDoc?.id]);

  const handleTabChange = async (tab) => {
    setActiveDocTab(tab);
    if (!selectedDoc) return;
    const audience = tab === 'customer' ? 'customer' : tab === 'internal' ? 'internal' : null;
    if (!audience) return;
    const field = `${audience}_content`;
    if (selectedDoc[field]) return;
    setGeneratingAudience(audience);
    try {
      const res = await axios.post(`${API_URL_BASE}/documentation/${selectedDoc.id}/generate-audience?audience=${audience}`);
      setSelectedDoc(prev => ({ ...prev, [field]: res.data.content }));
      if (!res.data.cached) toast.success(`${audience === 'customer' ? 'Customer' : 'Internal'} documentation generated!`);
    } catch (error) { toast.error(`Failed to generate ${audience} documentation`); }
    finally { setGeneratingAudience(null); }
  };

  const handleRegenerateAudience = async (audience) => {
    if (!selectedDoc) return;
    const field = `${audience}_content`;
    setGeneratingAudience(audience);
    try {
      const res = await axios.post(`${API_URL_BASE}/documentation/${selectedDoc.id}/generate-audience?audience=${audience}&force=true`);
      setSelectedDoc(prev => ({ ...prev, [field]: res.data.content }));
      toast.success(`${audience === 'customer' ? 'Customer' : 'Internal'} documentation regenerated!`);
    } catch (error) { toast.error(`Failed to regenerate ${audience} documentation`); }
    finally { setGeneratingAudience(null); }
  };

  const handleDeleteDoc = async (docId, title) => {
    if (canDelete) {
      if (!window.confirm(`Delete documentation "${title}"?`)) return;
      try { await deleteDocumentation(docId); toast.success('Documentation deleted'); setSelectedDoc(null); fetchData(); }
      catch (error) { toast.error('Failed to delete documentation'); }
    } else if (canRequestDelete) {
      const reason = window.prompt(`Request deletion of "${title}"?\nEnter a reason (optional):`);
      if (reason === null) return;
      try { await axios.post(`${API_URL_BASE}/delete-requests`, { doc_id: docId, reason }); toast.success('Delete request submitted for admin approval'); }
      catch (error) { toast.error(error.response?.data?.detail || 'Failed to submit delete request'); }
    }
  };

  // One export route for every document kind: the server builds SOW and design
  // documents branded (the plain export-word/-pdf routes 400 on them) and
  // applies the audience tab to plain documentation only.
  const handleExport = (fmt) => {
    if (!selectedDoc || !token) return;
    const audience = !isBrandedDoc && activeDocTab !== 'full' ? activeDocTab : null;
    const suffix = audience ? ` (${audience === 'customer' ? 'Customer' : 'Internal'})` : '';
    const label = fmt === 'pdf' ? 'PDF' : 'Word';
    const url = exportDocument(selectedDoc.id, fmt) + (audience ? `?audience=${audience}` : '');
    toast.promise(downloadExport(url, token, `${selectedDoc.title}${suffix}.${fmt === 'pdf' ? 'pdf' : 'docx'}`), {
      loading: `Preparing ${label}${suffix}…`,
      success: `Document exported as ${label}${suffix}!`,
      error: (err) => err.message,
    });
    setExportMenuOpen(false);
  };
  const handleExportWord = () => handleExport('word');
  const handleExportPdf = () => handleExport('pdf');

  const handleRegenerate = async () => {
    if (!selectedDoc || regenerating) return;
    if (!window.confirm('Regenerate this documentation? A new version will be created.')) return;
    setRegenerating(true);
    setRegenElapsedMin(0);
    const startTime = Date.now();
    if (regenTimerRef.current) clearInterval(regenTimerRef.current);
    regenTimerRef.current = setInterval(() => {
      setRegenElapsedMin(Math.floor((Date.now() - startTime) / 60000));
    }, 30000);
    try {
      const res = await regenerateDocumentation(selectedDoc.id);
      const jobId = res.data.job_id;
      const newDocId = res.data.new_doc_id;
      toast.info('Regeneration started...');
      const poll = setInterval(async () => {
        try {
          const jobRes = await getJobStatus(jobId);
          const job = jobRes.data;
          if (job.status === 'complete') {
            clearInterval(poll); clearInterval(regenTimerRef.current); regenTimerRef.current = null;
            setRegenerating(false); setRegenElapsedMin(0);
            toast.success('Documentation regenerated!'); await fetchData(); await loadFullDoc(newDocId);
          } else if (job.status === 'failed') {
            clearInterval(poll); clearInterval(regenTimerRef.current); regenTimerRef.current = null;
            setRegenerating(false); setRegenElapsedMin(0);
            if (job.error && (job.error.includes('interrupted') || job.error.includes('timeout'))) {
              toast.error('Regeneration was interrupted. Please try again.');
            } else {
              toast.error(job.error || 'Regeneration failed');
            }
          }
        } catch { clearInterval(poll); clearInterval(regenTimerRef.current); regenTimerRef.current = null; setRegenerating(false); setRegenElapsedMin(0); toast.error('Lost connection to regeneration job'); }
      }, 3000);
    } catch (error) { clearInterval(regenTimerRef.current); regenTimerRef.current = null; setRegenerating(false); setRegenElapsedMin(0); toast.error(error.response?.data?.detail || 'Failed to start regeneration'); }
  };

  // Current content for the active tab
  const activeContent = useMemo(() => {
    if (!selectedDoc) return '';
    if (activeDocTab === 'full') return selectedDoc.content || '';
    if (activeDocTab === 'customer') return selectedDoc.customer_content || '';
    if (activeDocTab === 'internal') return selectedDoc.internal_content || '';
    return '';
  }, [selectedDoc, activeDocTab]);

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-screen">
          <Loader2 className="h-8 w-8 animate-spin text-[#2B8FD8]" />
        </div>
      </Layout>
    );
  }

  // Reserve horizontal space matching the width of whichever fixed right panel
  // is open, so the panel pushes content instead of covering it (lg and up).
  // Panel widths: Chat 380/420(xl) · Webhook 520 · History/Source 480/560(lg) · Feedback 420
  const rightPanelPad = chatOpen
    ? 'lg:pr-[380px] xl:pr-[420px]'
    : webhookOpen
      ? 'lg:pr-[520px]'
      : (versionHistoryOpen || sourceFilesOpen)
        ? 'lg:pr-[480px] xl:pr-[560px]'
        : feedbackOpen
          ? 'lg:pr-[420px]'
          : '';

  return (
    <Layout>
      <div className={`flex flex-col min-h-screen transition-[padding-right] duration-300 ${rightPanelPad}`}>
        {/* Header — compact breadcrumb bar */}
        <div className="px-3 md:px-6 py-2.5 md:py-3 border-b border-slate-200 bg-white sticky top-0 z-10">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-1.5 md:gap-2 text-sm min-w-0">
              <Link to="/projects" className="text-slate-500 hover:text-[#2B8FD8] transition-colors flex items-center gap-1 shrink-0">
                <ArrowLeft className="h-3.5 w-3.5" />
                <span className="hidden sm:inline">Projects</span>
              </Link>
              <ChevronRight className="h-3 w-3 text-slate-300 shrink-0" />
              <div className="flex items-center gap-1.5 min-w-0">
                {!sidebarCollapsed ? null : (
                  <button onClick={() => setSidebarCollapsed(false)} className="p-1 rounded hover:bg-slate-100 text-slate-400 shrink-0 md:hidden" data-testid="mobile-sidebar-toggle">
                    <List className="h-4 w-4" />
                  </button>
                )}
                <FolderOpen className="h-4 w-4 text-emerald-600 shrink-0" />
                <span className="font-semibold text-slate-900 truncate" data-testid="project-name">{project?.name}</span>
              </div>
              {project?.integration_type && (
                <Badge variant="outline" className="text-[10px] ml-1 hidden sm:inline-flex">{project.integration_type}</Badge>
              )}
              <span className="text-slate-300 mx-1 hidden md:inline">|</span>
              <span className="text-slate-400 items-center gap-1 hidden xl:flex">
                <Building2 className="h-3 w-3" />{project?.customer_name}
              </span>
              <span className="text-slate-400 items-center gap-1 hidden xl:flex">
                <FileText className="h-3 w-3" />{documentation.length} docs
              </span>
            </div>
            <div className="flex items-center gap-1.5 md:gap-2 shrink-0">
              {canEdit && (
                <Button variant="outline" size="sm" onClick={() => { setWebhookOpen(true); setChatOpen(false); }} data-testid="webhook-btn"
                  className="border-emerald-200 text-emerald-700 hover:bg-emerald-50 h-8 text-xs">
                  <Webhook className="h-3.5 w-3.5 lg:mr-1.5" /><span className="hidden lg:inline">Auto-Sync</span>
                </Button>
              )}
              {canUpload && (
                <Link to={`/documentation/upload?project=${id}`}>
                  <Button className="bg-[#2B8FD8] hover:bg-[#2075b3] h-8 text-xs" data-testid="upload-docs-btn">
                    <Upload className="h-3.5 w-3.5 lg:mr-1.5" /><span className="hidden lg:inline">Upload</span>
                  </Button>
                </Link>
              )}
            </div>
          </div>
        </div>

        {/* Content Area */}
        {documentation.length > 0 ? (
          <div className="flex-1 flex relative">
            {/* Mobile sidebar overlay backdrop */}
            {!sidebarCollapsed && (
              <div className="fixed inset-0 bg-black/20 z-20 md:hidden" onClick={() => setSidebarCollapsed(true)} />
            )}

            {/* Doc Sidebar — collapsible, overlay on mobile */}
            <div className={`shrink-0 border-r border-slate-200 bg-white transition-all duration-300 z-30
              ${sidebarCollapsed ? 'w-0 overflow-hidden' : 'w-[240px]'}
              ${sidebarCollapsed ? '' : 'fixed md:relative inset-y-0 left-0 md:left-auto md:inset-y-auto'}
            `}>
              <div className="sticky top-[52px]">
                <div className="p-3 border-b border-slate-100 flex items-center justify-between">
                  <div>
                    <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-widest">Documents</p>
                    <p className="text-[10px] text-slate-400 mt-0.5">{documentation.length} files</p>
                  </div>
                  <button onClick={() => setSidebarCollapsed(true)} className="p-1 rounded hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors" data-testid="collapse-sidebar-btn">
                    <PanelLeftClose className="h-4 w-4" />
                  </button>
                </div>
                <div className="max-h-[calc(100vh-130px)] overflow-y-auto p-2 space-y-0.5">
                  {documentation.map((doc) => (
                    <button
                      key={doc.id}
                      onClick={() => { handleSelectDoc(doc); if (window.innerWidth < 768) setSidebarCollapsed(true); }}
                      className={`w-full text-left px-3 py-2.5 rounded-md transition-all group ${
                        selectedDoc?.id === doc.id
                          ? 'bg-blue-50 ring-1 ring-[#2B8FD8]/20'
                          : 'hover:bg-slate-50'
                      }`}
                      data-testid={`doc-item-${doc.id}`}
                    >
                      <div className="flex items-center gap-2">
                        <FileText className={`h-3.5 w-3.5 shrink-0 ${selectedDoc?.id === doc.id ? 'text-[#2B8FD8]' : 'text-slate-400'}`} />
                        <span className={`text-sm font-medium truncate ${selectedDoc?.id === doc.id ? 'text-[#2B8FD8]' : 'text-slate-700'}`}>
                          {doc.title}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 mt-1 ml-5.5 text-[10px] text-slate-400">
                        <span>v{doc.version}</span>
                        <span>{new Date(doc.created_at).toLocaleDateString()}</span>
                        {doc.superseded_by && (
                          <span className="px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-500 text-[9px] font-medium">superseded</span>
                        )}
                        {doc.is_latest === true && (
                          <span className="px-1.5 py-0.5 rounded-full bg-emerald-50 text-emerald-600 text-[9px] font-medium">latest</span>
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Sidebar expand button (visible when collapsed, desktop only — mobile uses header toggle) */}
            {sidebarCollapsed && (
              <button
                onClick={() => setSidebarCollapsed(false)}
                className="fixed left-[256px] top-[60px] z-20 bg-white border border-slate-200 rounded-r-lg p-1.5 shadow-sm hover:bg-slate-50 transition-colors hidden md:block"
                data-testid="expand-sidebar-btn"
              >
                <PanelLeftOpen className="h-4 w-4 text-slate-500" />
              </button>
            )}

            {/* Main Content + ToC */}
            <div className="flex-1 min-w-0 flex" ref={contentRef}>
              {/* Center content */}
              <div className="flex-1 min-w-0 bg-slate-50/50">
                {loadingDoc ? (
                  <div className="flex items-center justify-center py-32">
                    <Loader2 className="h-8 w-8 animate-spin text-[#2B8FD8]" />
                  </div>
                ) : selectedDoc ? (
                  <div>
                    {/* Doc toolbar — slim action bar */}
                    <div className="px-3 md:px-6 py-2 md:py-2.5 bg-white border-b border-slate-100 flex items-start md:items-center justify-between gap-2 overflow-x-auto">
                      <div className="flex items-center gap-2 md:gap-3 min-w-0 overflow-hidden">
                        <h2 className="text-base md:text-lg font-semibold text-slate-900 font-['Plus_Jakarta_Sans'] truncate">
                          {selectedDoc.title}
                        </h2>
                        <Badge variant="outline" className="text-[10px] shrink-0">v{selectedDoc.version}</Badge>
                        <span className="text-xs text-slate-400 shrink-0 hidden xl:inline">
                          {new Date(selectedDoc.created_at).toLocaleDateString()} by {selectedDoc.created_by_name || selectedDoc.created_by}
                        </span>
                      </div>
                      <div className="flex items-center gap-1 md:gap-1.5 shrink-0 pl-1">
                        {canEdit && (
                          <Button variant="ghost" size="sm" onClick={handleRegenerate} disabled={regenerating} data-testid="regenerate-btn" className="h-7 text-xs text-slate-500 px-1.5 md:px-3">
                            {regenerating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
                            <span className="hidden xl:inline ml-1">{regenerating ? 'Regenerating...' : 'Regenerate'}</span>
                          </Button>
                        )}
                        {/* SOW and design documents export branded, which the server
                            only allows editors and admins — hide the buttons from
                            readers rather than let them fail. */}
                        {(!isBrandedDoc || canEdit) && (
                          <>
                            <Button variant="ghost" size="sm" onClick={handleExportWord} data-testid="export-word-btn" className="h-7 text-xs text-slate-500 px-1.5 md:px-3">
                              <Download className="h-3.5 w-3.5" /><span className="hidden xl:inline ml-1">Word</span>
                            </Button>
                            <Button variant="ghost" size="sm" onClick={handleExportPdf} data-testid="export-pdf-btn" className="h-7 text-xs text-slate-500 px-1.5 md:px-3">
                              <FileDown className="h-3.5 w-3.5" /><span className="hidden xl:inline ml-1">PDF</span>
                            </Button>
                          </>
                        )}
                        <Button variant="ghost" size="sm" onClick={() => { setVersionHistoryOpen(true); setChatOpen(false); setWebhookOpen(false); setSourceFilesOpen(false); }} data-testid="version-history-btn" className="h-7 text-xs text-slate-500 px-1.5 md:px-3">
                          <History className="h-3.5 w-3.5" /><span className="hidden md:inline ml-1">History</span>
                        </Button>
                        {selectedDoc?.files_analyzed > 0 && (
                          <Button variant="ghost" size="sm" onClick={() => { setSourceFilesOpen(true); setChatOpen(false); setWebhookOpen(false); setVersionHistoryOpen(false); }} data-testid="source-files-btn" className="h-7 text-xs text-slate-500 px-1.5 md:px-3">
                            <Code2 className="h-3.5 w-3.5" /><span className="hidden xl:inline ml-1">Source</span>
                          </Button>
                        )}
                        <Button variant="ghost" size="sm"
                          onClick={() => { setFeedbackOpen(true); setFeedbackSection(''); setChatOpen(false); setWebhookOpen(false); setVersionHistoryOpen(false); setSourceFilesOpen(false); }}
                          data-testid="feedback-btn"
                          className="h-7 text-xs text-slate-500 px-1.5 md:px-3 relative"
                        >
                          <MessageSquarePlus className="h-3.5 w-3.5" /><span className="hidden xl:inline ml-1">Feedback</span>
                          {openFeedbackCount > 0 && (
                            <span className="absolute -top-1 -right-1 min-w-[16px] h-4 rounded-full bg-[#2B8FD8] text-white text-[9px] font-bold flex items-center justify-center px-1">
                              {openFeedbackCount}
                            </span>
                          )}
                        </Button>
                        {(canDelete || canRequestDelete) && (
                          <Button variant="ghost" size="sm"
                            className={`h-7 px-1.5 ${canDelete ? "text-slate-400 hover:text-red-500" : "text-slate-400 hover:text-amber-500"}`}
                            onClick={() => handleDeleteDoc(selectedDoc.id, selectedDoc.title)}
                            data-testid="delete-doc-btn" title={canDelete ? 'Delete' : 'Request deletion'}>
                            {canDelete ? <Trash2 className="h-3.5 w-3.5" /> : <Send className="h-3.5 w-3.5" />}
                          </Button>
                        )}
                      </div>
                    </div>

                    {/* AI Summary — inline, subtle */}
                    {selectedDoc.ai_summary && (
                      <div className="max-w-3xl mx-auto px-4 md:px-10 pt-4 md:pt-6">
                        <div className="bg-sky-50/60 border border-sky-100 rounded-lg px-4 md:px-5 py-3">
                          <p className="text-[10px] font-semibold text-[#2B8FD8] uppercase tracking-widest mb-1.5">Summary</p>
                          <p className="text-sm text-slate-600 leading-relaxed">{selectedDoc.ai_summary}</p>
                        </div>
                      </div>
                    )}

                    {/* Regeneration in progress banner */}
                    {regenerating && (
                      <div className="max-w-3xl mx-auto px-4 md:px-10 pt-4">
                        <div className={`rounded-lg px-4 py-3 flex items-start gap-3 ${regenElapsedMin >= SLOW_AFTER_MIN ? 'bg-amber-50 border border-amber-200' : 'bg-blue-50 border border-blue-200'}`}>
                          <Loader2 className={`h-4 w-4 animate-spin mt-0.5 shrink-0 ${regenElapsedMin >= SLOW_AFTER_MIN ? 'text-amber-600' : 'text-blue-600'}`} />
                          <div>
                            <p className={`text-sm font-medium ${regenElapsedMin >= SLOW_AFTER_MIN ? 'text-amber-700' : 'text-blue-700'}`}>
                              {regenElapsedMin >= SLOW_AFTER_MIN ? 'Regeneration is taking longer than usual' : 'Regenerating documentation...'}
                            </p>
                            <p className={`text-xs mt-0.5 ${regenElapsedMin >= SLOW_AFTER_MIN ? 'text-amber-600' : 'text-blue-500'}`}>
                              {regenElapsedMin >= SLOW_AFTER_MIN
                                ? `Running for ${regenElapsedMin} minutes. Leave it running for now; if it has not finished by 20 minutes, reload the page and regenerate again.`
                                : `AI is re-analysing the source code. This can take several minutes, depending on how much there is to read (${regenElapsedMin >= 1 ? `${regenElapsedMin} min so far` : 'just started'}). You can leave this page open — the new version is saved when it finishes.`}
                            </p>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Staleness warning banner (Issue 1) */}
                    {selectedDoc?.staleness?.needs_regeneration && !regenerating && (
                      <div className="max-w-3xl mx-auto px-4 md:px-10 pt-4">
                        <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 flex items-start gap-3">
                          <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-amber-700">
                              Quality drift detected
                            </p>
                            <p className="text-xs text-amber-600 mt-0.5">
                              This doc has been auto-patched {selectedDoc.staleness.incremental_update_count} times since last full generation.
                              Consider regenerating for a clean, well-structured version.
                            </p>
                          </div>
                          {canEdit && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={handleRegenerate}
                              className="shrink-0 h-7 text-xs border-amber-300 text-amber-700 hover:bg-amber-100"
                              data-testid="staleness-regenerate-btn"
                            >
                              <RefreshCw className="h-3 w-3 mr-1" />
                              Regenerate
                            </Button>
                          )}
                        </div>
                      </div>
                    )}

                    {/* View tabs */}
                    <Tabs value={activeDocTab} onValueChange={handleTabChange} className="w-full">
                      <div className="max-w-3xl mx-auto px-4 md:px-10 pt-4 md:pt-5">
                        <TabsList className="h-11 w-full md:w-auto" data-testid="doc-tabs">
                          <TabsTrigger value="full" className="flex items-center gap-1.5 flex-1 md:flex-none" data-testid="tab-full">
                            <FileText className="h-3.5 w-3.5" /><span className="hidden sm:inline">Full </span>Docs
                          </TabsTrigger>
                          <TabsTrigger value="customer" className="flex items-center gap-1.5 flex-1 md:flex-none" data-testid="tab-customer">
                            <Users className="h-3.5 w-3.5" /> Customer
                            {selectedDoc && !selectedDoc.customer_content && selectedDoc.version > 1 && (
                              <span className="ml-1 w-2 h-2 rounded-full bg-amber-400 inline-block" title="Content needs regeneration — was cleared after a doc update" />
                            )}
                          </TabsTrigger>
                          <TabsTrigger value="internal" className="flex items-center gap-1.5 flex-1 md:flex-none" data-testid="tab-internal">
                            <Wrench className="h-3.5 w-3.5" /> Internal
                            {selectedDoc && !selectedDoc.internal_content && selectedDoc.version > 1 && (
                              <span className="ml-1 w-2 h-2 rounded-full bg-amber-400 inline-block" title="Content needs regeneration — was cleared after a doc update" />
                            )}
                          </TabsTrigger>
                        </TabsList>
                      </div>

                      {/* Full Documentation */}
                      <TabsContent value="full" className="mt-0">
                        <div className="max-w-3xl mx-auto px-3 md:px-10 py-4 md:py-8">
                          <article className="bg-white rounded-xl border border-slate-200/80 shadow-sm px-4 py-6 md:px-10 md:py-10 lg:px-14 lg:py-12">
                            <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
                              {selectedDoc.content}
                            </ReactMarkdown>
                          </article>
                        </div>
                      </TabsContent>

                      {/* Customer View */}
                      <TabsContent value="customer" className="mt-0">
                        <div className="max-w-3xl mx-auto px-3 md:px-10 py-4 md:py-8">
                          {selectedDoc.customer_content && generatingAudience !== 'customer' && canEdit && (
                            <div className="flex justify-end mb-2">
                              <Button variant="outline" size="sm" onClick={() => handleRegenerateAudience('customer')}
                                className="text-xs text-slate-500 hover:text-[#2B8FD8]">
                                <RefreshCw className="h-3 w-3 mr-1.5" /> Regenerate
                              </Button>
                            </div>
                          )}
                          <article className="bg-white rounded-xl border border-slate-200/80 shadow-sm px-4 py-6 md:px-10 md:py-10 lg:px-14 lg:py-12">
                            {generatingAudience === 'customer' ? (
                              <div className="flex flex-col items-center justify-center py-20">
                                <Loader2 className="h-8 w-8 animate-spin text-[#2B8FD8] mb-4" />
                                <p className="text-sm font-medium text-slate-700">Generating customer documentation...</p>
                                <p className="text-xs text-slate-400 mt-1">AI is rewriting for a business audience</p>
                                <p className="text-xs text-slate-400 mt-0.5">This may take 30-60 seconds</p>
                              </div>
                            ) : selectedDoc.customer_content ? (
                              <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
                                {selectedDoc.customer_content}
                              </ReactMarkdown>
                            ) : (
                              <div className="flex flex-col items-center justify-center py-20 text-slate-400">
                                <Users className="h-10 w-10 mb-3" />
                                <p className="text-sm">Customer view will be generated when you switch to this tab</p>
                              </div>
                            )}
                          </article>
                        </div>
                      </TabsContent>

                      {/* Internal/Technical View */}
                      <TabsContent value="internal" className="mt-0">
                        <div className="max-w-3xl mx-auto px-3 md:px-10 py-4 md:py-8">
                          {selectedDoc.internal_content && generatingAudience !== 'internal' && canEdit && (
                            <div className="flex justify-end mb-2">
                              <Button variant="outline" size="sm" onClick={() => handleRegenerateAudience('internal')}
                                className="text-xs text-slate-500 hover:text-[#2B8FD8]">
                                <RefreshCw className="h-3 w-3 mr-1.5" /> Regenerate
                              </Button>
                            </div>
                          )}
                          <article className="bg-white rounded-xl border border-slate-200/80 shadow-sm px-4 py-6 md:px-10 md:py-10 lg:px-14 lg:py-12">
                            {generatingAudience === 'internal' ? (
                              <div className="flex flex-col items-center justify-center py-20">
                                <Loader2 className="h-8 w-8 animate-spin text-[#2B8FD8] mb-4" />
                                <p className="text-sm font-medium text-slate-700">Generating internal documentation...</p>
                                <p className="text-xs text-slate-400 mt-1">AI is rewriting for a technical audience</p>
                                <p className="text-xs text-slate-400 mt-0.5">This may take 30-60 seconds</p>
                              </div>
                            ) : selectedDoc.internal_content ? (
                              <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
                                {selectedDoc.internal_content}
                              </ReactMarkdown>
                            ) : (
                              <div className="flex flex-col items-center justify-center py-20 text-slate-400">
                                <Wrench className="h-10 w-10 mb-3" />
                                <p className="text-sm">Internal view will be generated when you switch to this tab</p>
                              </div>
                            )}
                          </article>
                        </div>
                      </TabsContent>
                    </Tabs>
                  </div>
                ) : null}
              </div>

              {/* Right sidebar — Table of Contents (visible when content is available) */}
              {selectedDoc && activeContent && !loadingDoc && !generatingAudience && (
                // TOC yields to right panels below 2xl — there isn't room for both
                <div className={`w-[200px] shrink-0 hidden ${isRightPanelOpen ? '2xl:block' : 'xl:block'}`}>
                  <div className="sticky top-[100px] py-6 pr-4 max-h-[calc(100vh-120px)] overflow-y-auto">
                    <TableOfContents content={activeContent} activeId={activeTocId} />
                  </div>
                </div>
              )}
            </div>
          </div>
        ) : (
          /* Empty state */
          <div className="flex-1 flex items-center justify-center bg-slate-50">
            <div className="text-center">
              <FileText className="h-16 w-16 text-slate-200 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-slate-900 mb-2 font-['Plus_Jakarta_Sans']">No documentation yet</h3>
              <p className="text-sm text-slate-500 mb-6 max-w-sm mx-auto">
                Upload integration files to generate AI-powered documentation for this project
              </p>
              <Link to={`/documentation/upload?project=${id}`}>
                <Button className="bg-[#2B8FD8] hover:bg-[#2075b3]">
                  <Upload className="h-4 w-4 mr-2" />Upload Your First File
                </Button>
              </Link>
            </div>
          </div>
        )}
      </div>

      {/* AI Chat Panel */}
      <ChatPanel projectId={id} selectedDoc={selectedDoc} onDocUpdated={handleDocUpdated} isOpen={chatOpen} onClose={() => setChatOpen(false)} />

      {/* Webhook Panel */}
      <WebhookPanel projectId={id} documentation={documentation} isOpen={webhookOpen} onClose={() => setWebhookOpen(false)} />

      {/* Version History Panel */}
      <VersionHistoryPanel
        docId={selectedDoc?.id}
        docTitle={selectedDoc?.title}
        isOpen={versionHistoryOpen}
        onClose={() => setVersionHistoryOpen(false)}
        regenerationLineage={selectedDoc?.regeneration_lineage}
        onNavigateDoc={(targetDocId) => loadFullDoc(targetDocId)}
        onSelectVersion={canEdit ? async (content) => {
          try {
            await axios.patch(`${API_URL_BASE}/documentation/${selectedDoc.id}/content`, { content });
            toast.success('Version restored!');
            await loadFullDoc(selectedDoc.id);
          } catch { toast.error('Failed to restore version'); }
        } : null}
      />

      {/* Source Files Panel */}
      <SourceFilesPanel
        docId={selectedDoc?.id}
        isOpen={sourceFilesOpen}
        onClose={() => setSourceFilesOpen(false)}
      />

      {/* Feedback Panel (Issue 4) */}
      <FeedbackPanel
        docId={selectedDoc?.id}
        docContent={activeContent}
        docVersion={selectedDoc?.version}
        isOpen={feedbackOpen}
        onClose={() => { setFeedbackOpen(false); setFeedbackSection(''); }}
        initialSection={feedbackSection}
        onFeedbackCountChange={handleFeedbackCountChange}
        onApplied={() => selectedDoc && loadFullDoc(selectedDoc.id)}
      />

      {/* Floating Chat Toggle */}
      {!chatOpen && !webhookOpen && !versionHistoryOpen && !sourceFilesOpen && !feedbackOpen && (
        <button
          onClick={() => { setChatOpen(true); setWebhookOpen(false); }}
          className="fixed bottom-6 right-6 z-40 w-12 h-12 rounded-full bg-[#2B8FD8] text-white shadow-lg hover:bg-[#2075b3] transition-all hover:scale-105 flex items-center justify-center group"
          data-testid="chat-toggle-btn"
        >
          <Sparkles className="h-5 w-5 group-hover:rotate-12 transition-transform" />
        </button>
      )}
    </Layout>
  );
};

export default ProjectDetailPage;
